import { createSelector } from 'reselect';
import {
    isAfter,
    differenceInHours,
    addHours,
    differenceInMinutes,
    differenceInSeconds,
    addMinutes,
} from 'date-fns';

import * as appSelectors from './app';

const key = 'cart';
const domains = {
    activities: (state) => state.global[key].activities,
    data: (state) => state.global[key].data,
    dataId: (state) => state.global[key].data.id,
    errorMessage: (state) => state.global[key].errorMessage,
    expireModal: (state) => state.global[key].expireModal,
    isApplying: (state) => state.global[key].isApplying,
    isApplyingError: (state) => state.global[key].isApplyingError,
    isError: (state) => state.global[key].isError,
    isExtending: (state) => state.global[key].isExtending,
    isLoaded: (state) => state.global[key].isLoaded,
    isLoading: (state) => state.global[key].isLoading,
    isOutdatedLoaded: (state) => state.global[key].isOutdatedLoaded,
    itemProductIds: (state) => state.global[key].itemProductIds,
    items: (state) => state.global[key].items,
    itemsStates: (state) => state.global[key].itemsStates,
    promocodeValue: (state) => state.global[key].promocodeValue,
    sessionId: (state) => state.global[key].sessionId,
};

const selectorIsExpired = createSelector(
    [domains.data, appSelectors.makeSelectTick()],
    (cartDataState) => {
        if (!cartDataState.id) {
            return true;
        }

        if (!cartDataState.expireTime) {
            return true;
        }

        const startDate = new Date();
        const endDate = new Date(cartDataState.expireTime);

        return isAfter(startDate, endDate);
    }
);

const selectorItems = createSelector(
    [domains.items, domains.itemsStates, selectorIsExpired],
    (items, itemsStates, isExpired) => {
        if (isExpired) {
            return [];
        }
        return items.map((item) => ({
            ...item,
            state: {
                isChanging:
                    (itemsStates[item.id] ? itemsStates[item.id].isChanging : false) || false,
                isRemoving:
                    (itemsStates[item.id] ? itemsStates[item.id].isRemoving : false) || false,
            },
        }));
    }
);

const selectorIsSad = createSelector(
    [domains.dataId, domains.isLoaded, domains.isError],
    (cartDataId, isLoaded, isError) => Boolean(!parseInt(cartDataId, 10) && isLoaded && !isError)
);

const selectorActivities = createSelector([domains.activities], (activities) =>
    activities.map((activity) => ({
        ...activity,
        reviews: (activity.reviews || []).filter((review) => Boolean(review.content)).slice(0, 3),
    }))
);

const selectorData = createSelector([domains.data], (data) => data);

const selectorItemProductIds = createSelector([domains.itemProductIds], (itemProductIds) =>
    Array.isArray(itemProductIds) ? itemProductIds : []
);

const selectorExpireDiff = createSelector(
    [domains.data, selectorIsExpired, appSelectors.makeSelectTick()],
    (cartDataState, isExpired) => {
        if (isExpired) {
            return {
                hours: 0,
                minutes: 0,
                seconds: 0,
                totalMinutes: 0,
                totalSeconds: 0,
            };
        }

        const endDate = new Date(cartDataState.expireTime);

        const now = new Date();
        const hours = differenceInHours(endDate, now);
        const minutes = differenceInMinutes(endDate, addHours(now, hours));
        const seconds = differenceInSeconds(endDate, addMinutes(now, minutes));

        return {
            hours,
            minutes,
            seconds,
            totalMinutes: differenceInMinutes(endDate, now),
            totalSeconds: differenceInSeconds(endDate, now),
        };
    }
);

const selectorIsLoading = createSelector([domains.isLoading], (isLoading) => isLoading);

const selectorIsLoaded = createSelector([domains.isLoaded], (isLoaded) => isLoaded);

const selectorIsError = createSelector([domains.isError], (isError) => isError);

const selectorIsOutdated = createSelector(
    [domains.itemProductIds, selectorIsSad],
    (itemProductIds, isSad) => Array.isArray(itemProductIds) && itemProductIds.length > 0 && isSad
);

const selectorIsOutdatedLoaded = createSelector(
    [domains.isOutdatedLoaded],
    (isOutdatedLoaded) => isOutdatedLoaded
);

const selectorIsExtending = createSelector([domains.isExtending], (isExtending) => isExtending);

const selectorIsApplying = createSelector([domains.isApplying], (isApplying) => isApplying);

const selectorIsApplyingError = createSelector(
    [domains.isApplyingError],
    (isApplyingError) => isApplyingError
);

const selectorErrorMessage = createSelector([domains.errorMessage], (errorMessage) => errorMessage);

const selectorPromocodeValue = createSelector(
    [domains.promocodeValue],
    (promocodeValue) => promocodeValue
);

const selectorSessionId = createSelector([domains.sessionId], (sessionId) => sessionId);

const selectorExpireModalIsVisible = createSelector(
    [domains.expireModal],
    (expireModal) => expireModal.isVisible
);

const makeSelectIsExpired = () => selectorIsExpired;
const makeSelectItems = () => selectorItems;
const makeSelectIsSad = () => selectorIsSad;
const makeSelectActivities = () => selectorActivities;
const makeSelectData = () => selectorData;
const makeSelectItemProductIds = () => selectorItemProductIds;
const makeSelectExpireDiff = () => selectorExpireDiff;
const makeSelectIsLoading = () => selectorIsLoading;
const makeSelectIsLoaded = () => selectorIsLoaded;
const makeSelectIsError = () => selectorIsError;
const makeSelectIsOutdated = () => selectorIsOutdated;
const makeSelectIsOutdatedLoaded = () => selectorIsOutdatedLoaded;
const makeSelectIsExtending = () => selectorIsExtending;
const makeSelectIsApplying = () => selectorIsApplying;
const makeSelectIsApplyingError = () => selectorIsApplyingError;
const makeSelectErrorMessage = () => selectorErrorMessage;
const makeSelectPromocodeValue = () => selectorPromocodeValue;
const makeSelectSessionId = () => selectorSessionId;
const makeSelectExpireModalIsVisible = () => selectorExpireModalIsVisible;

export {
    makeSelectActivities,
    makeSelectData,
    makeSelectErrorMessage,
    makeSelectExpireDiff,
    makeSelectExpireModalIsVisible,
    makeSelectIsApplying,
    makeSelectIsApplyingError,
    makeSelectIsError,
    makeSelectIsExpired,
    makeSelectIsExtending,
    makeSelectIsLoaded,
    makeSelectIsLoading,
    makeSelectIsOutdated,
    makeSelectIsOutdatedLoaded,
    makeSelectIsSad,
    makeSelectItemProductIds,
    makeSelectItems,
    makeSelectPromocodeValue,
    makeSelectSessionId,
};
